import { useRef, useState, useEffect } from 'react';
import { useI18n } from '@/shared/i18n';
import {
  Loader2,
  Plus,
  LogIn,
  LogOut,
  Filter,
  Car,
  Camera,
  User,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useAccessLogs } from '../hooks';
import { ShiftRequiredGuard } from '../components';
import { useAbility } from '@/modules/identity/application/ability';
import type { AccessType, AccessStatus } from '@yawi/types';
import type { GuardAccessType, GuardAccessMethod, GuardPersonType, CreateGuardAccessLogDTO } from '@yawi/types';

const ACCESS_TYPES: { value: GuardAccessType; labelKey: string; icon: typeof LogIn }[] = [
  { value: 'ENTRY', labelKey: 'accessLogs.type.entry', icon: LogIn },
  { value: 'EXIT', labelKey: 'accessLogs.type.exit', icon: LogOut },
];

const ACCESS_METHODS_FORM: { value: GuardAccessMethod; labelKey: string }[] = [
  { value: 'MANUAL', labelKey: 'accessLogs.method.manual' },
  { value: 'QR', labelKey: 'accessLogs.method.qr' },
  { value: 'RFID', labelKey: 'accessLogs.method.rfid' },
  { value: 'FACIAL', labelKey: 'accessLogs.method.facial' },
  { value: 'PLATE', labelKey: 'accessLogs.method.plate' },
];

const ACCESS_METHODS: { value: AccessType; label: string }[] = [
  { value: 'QR_RESIDENT' as AccessType, label: 'QR Residente' },
  { value: 'QR_GUEST' as AccessType, label: 'QR Invitado' },
  { value: 'MANUAL' as AccessType, label: 'Manual' },
  { value: 'APP_REMOTE' as AccessType, label: 'App Remota' },
  { value: 'RFID' as AccessType, label: 'RFID' },
  { value: 'PIN' as AccessType, label: 'PIN' },
  { value: 'REMOTE_OPEN' as AccessType, label: 'Apertura Remota' },
  { value: 'FACEID' as AccessType, label: 'Reconocimiento Facial' },
];

const ACCESS_STATUSES: { value: AccessStatus; label: string }[] = [
  { value: 'GRANTED' as AccessStatus, label: 'Concedido' },
  { value: 'DENIED' as AccessStatus, label: 'Denegado' },
];

// Method labels for display
const METHOD_LABELS: Record<string, string> = {
  QR_RESIDENT: 'QR Residente',
  QR_GUEST: 'QR Invitado',
  MANUAL: 'Manual',
  APP_REMOTE: 'App Remota',
  RFID: 'RFID',
  PIN: 'PIN',
  REMOTE_OPEN: 'Apertura Remota',
  FACEID: 'Reconocimiento Facial',
};

const isAdapterError = (raw: string) => /adapter_error|doControl|HikCentral|timeout/i.test(raw);

// Traduce errores técnicos del adaptador/dispositivo (HikCentral, etc.) a mensajes claros.
// Solo se muestran cuando el acceso fue denegado; en accesos concedidos se ocultan (null).
// Si no reconoce un error técnico devuelve el texto original.
const humanizeDetails = (
  details: string,
  status: string,
  t: (key: string) => string,
): string | null => {
  const raw = details.trim();
  if (!isAdapterError(raw)) return raw;
  if (status !== 'DENIED') return null;

  if (/timeout/i.test(raw)) return t('accessLogs.error.timeout');
  if (/code=64|user authentication failed/i.test(raw)) return t('accessLogs.error.authFailed');
  if (/code=68|signature/i.test(raw)) return t('accessLogs.error.signatureFailed');
  return t('accessLogs.error.generic');
};

const PERSON_TYPES: { value: GuardPersonType; labelKey: string }[] = [
  { value: 'RESIDENT', labelKey: 'accessLogs.personType.resident' },
  { value: 'VISITOR', labelKey: 'accessLogs.personType.visitor' },
  { value: 'DELIVERY', labelKey: 'accessLogs.personType.delivery' },
  { value: 'SERVICE', labelKey: 'accessLogs.personType.service' },
  { value: 'EMPLOYEE', labelKey: 'accessLogs.personType.employee' },
];

interface AccessLogFormData {
  type: GuardAccessType;
  method: GuardAccessMethod;
  personType: GuardPersonType;
  personName: string;
  personDocument: string;
  vehiclePlate: string;
  unitId: string;
  notes: string;
}

const initialFormData: AccessLogFormData = {
  type: 'ENTRY',
  method: 'MANUAL',
  personType: 'VISITOR',
  personName: '',
  personDocument: '',
  vehiclePlate: '',
  unitId: '',
  notes: '',
};

export const AccessLogsPage = () => (
  <ShiftRequiredGuard>
    <AccessLogsContent />
  </ShiftRequiredGuard>
);

const AccessLogsContent = () => {
  const { t } = useI18n();
  const ability = useAbility();
  const {
    accessLogs,
    pagination,
    loading,
    creating,
    error,
    entryPointOptions,
    params,
    filterByMethod,
    filterByStatus,
    filterByDate,
    filterByEntryPoint,
    filterByPerson,
    filterByDetails,
    clearFilters,
    goToPage,
    setPageSize,
    createAccessLog,
    refresh,
  } = useAccessLogs();
  const canReadAccessLog = ability.can('manage', 'all') || ability.can('manage', 'AccessLog') || ability.can('read', 'AccessLog');
  const canCreateAccessLog = ability.can('manage', 'all') || ability.can('manage', 'AccessLog') || ability.can('create', 'AccessLog');

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const lastRefreshRef = useRef<number>(0);

  const handleImgError = () => {
    const now = Date.now();
    if (now - lastRefreshRef.current > 3000) {
      lastRefreshRef.current = now;
      refresh();
    }
  };

  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<AccessLogFormData>(initialFormData);
  const [showFilters, setShowFilters] = useState(false);
  const [showAccessPointMenu, setShowAccessPointMenu] = useState(false);
  const [pageInput, setPageInput] = useState('1');

  useEffect(() => {
    if (pagination) setPageInput(String(pagination.page));
  }, [pagination?.page]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateAccessLog) return;

    const data: CreateGuardAccessLogDTO = {
      type: formData.type,
      method: formData.method,
      personType: formData.personType,
      personName: formData.personName,
      ...(formData.personDocument && { personDocument: formData.personDocument }),
      ...(formData.vehiclePlate && { vehiclePlate: formData.vehiclePlate }),
      ...(formData.unitId && { unitId: formData.unitId }),
      ...(formData.notes && { notes: formData.notes }),
    };

    try {
      await createAccessLog(data);
      setFormData(initialFormData);
      setShowForm(false);
    } catch {
      // Error is handled by hook
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMethodLabel = (method: string) => {
    return METHOD_LABELS[method] || method;
  };

  const hasActiveFilters = params.method || params.status || params.date || params.entryPoint || params.personSearch || params.detailSearch;

  if (!canReadAccessLog) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-slate-400 gap-3">
        <User className="w-12 h-12" />
        <p className="text-base font-medium">No tienes permisos para ver este contenido</p>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">{t('accessLogs.title')}</h2>
          <p className="text-slate-500 text-sm">{t('accessLogs.subtitle')}</p>
        </div>
        {canCreateAccessLog && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            {showForm ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showForm ? t('common.cancel') : t('accessLogs.create')}
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
          {error}
        </div>
      )}

      {/* Form */}
      {canCreateAccessLog && showForm && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm animate-in slide-in-from-top duration-300">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">{t('accessLogs.create')}</h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Access Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('accessLogs.accessType')}
              </label>
              <div className="grid grid-cols-2 gap-2">
                {ACCESS_TYPES.map(({ value, labelKey, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, type: value }))}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.type === value
                        ? value === 'ENTRY'
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {t(labelKey)}
                  </button>
                ))}
              </div>
            </div>

            {/* Method and Person Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('accessLogs.accessMethod')}
                </label>
                <select
                  value={formData.method}
                  onChange={(e) => setFormData(prev => ({ ...prev, method: e.target.value as GuardAccessMethod }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {ACCESS_METHODS_FORM.map(({ value, labelKey }) => (
                    <option key={value} value={value}>{t(labelKey)}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('accessLogs.personTypeLabel')}
                </label>
                <select
                  value={formData.personType}
                  onChange={(e) => setFormData(prev => ({ ...prev, personType: e.target.value as GuardPersonType }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {PERSON_TYPES.map(({ value, labelKey }) => (
                    <option key={value} value={value}>{t(labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Person Name and Document */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('accessLogs.personName')} *
                </label>
                <input
                  type="text"
                  required
                  value={formData.personName}
                  onChange={(e) => setFormData(prev => ({ ...prev, personName: e.target.value }))}
                  placeholder={t('accessLogs.personNamePlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {t('accessLogs.personDocument')}
                </label>
                <input
                  type="text"
                  value={formData.personDocument}
                  onChange={(e) => setFormData(prev => ({ ...prev, personDocument: e.target.value }))}
                  placeholder={t('accessLogs.personDocumentPlaceholder')}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Vehicle Plate */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('accessLogs.vehiclePlate')}
              </label>
              <input
                type="text"
                value={formData.vehiclePlate}
                onChange={(e) => setFormData(prev => ({ ...prev, vehiclePlate: e.target.value.toUpperCase() }))}
                placeholder={t('accessLogs.vehiclePlatePlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('accessLogs.notes')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder={t('accessLogs.notesPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>

            <button
              type="submit"
              disabled={creating || !formData.personName}
              className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              {t('accessLogs.registerAccess')}
            </button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              hasActiveFilters
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-slate-300 hover:bg-slate-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            {t('common.filters')}
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 animate-in slide-in-from-top duration-200">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.accessMethod')}</label>
              <select
                value={params.method || ''}
                onChange={(e) => filterByMethod(e.target.value as AccessType || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="">{t('common.all')}</option>
                {ACCESS_METHODS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.status')}</label>
              <select
                value={params.status || ''}
                onChange={(e) => filterByStatus(e.target.value as AccessStatus || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              >
                <option value="">{t('common.all')}</option>
                {ACCESS_STATUSES.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.personSearch')}</label>
              <input
                type="search"
                value={params.personSearch || ''}
                onChange={(e) => filterByPerson(e.target.value || undefined)}
                placeholder={t('accessLogs.personSearchPlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.detailSearch')}</label>
              <input
                type="search"
                value={params.detailSearch || ''}
                onChange={(e) => filterByDetails(e.target.value || undefined)}
                placeholder={t('accessLogs.detailSearchPlaceholder')}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.entryPoint')}</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowAccessPointMenu((prev) => !prev)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-left flex items-center justify-between gap-2"
                >
                  <span className={params.entryPoint ? 'text-slate-800' : 'text-slate-400'}>
                    {params.entryPoint || t('accessLogs.entryPointPlaceholder')}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showAccessPointMenu ? 'rotate-180' : ''}`} />
                </button>

                {showAccessPointMenu && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-20 max-h-56 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                    <button
                      type="button"
                      onClick={() => {
                        filterByEntryPoint(undefined);
                        setShowAccessPointMenu(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                        !params.entryPoint ? 'text-primary-700 bg-primary-50' : 'text-slate-700'
                      }`}
                    >
                      {t('common.all')}
                    </button>
                    {entryPointOptions.map((entryPoint) => (
                      <button
                        key={entryPoint}
                        type="button"
                        onClick={() => {
                          filterByEntryPoint(entryPoint);
                          setShowAccessPointMenu(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 ${
                          params.entryPoint === entryPoint ? 'text-primary-700 bg-primary-50' : 'text-slate-700'
                        }`}
                      >
                        {entryPoint}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">{t('accessLogs.date')}</label>
              <input
                type="date"
                value={params.date || ''}
                onChange={(e) => filterByDate(e.target.value || undefined)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm"
              />
            </div>

            {hasActiveFilters && (
              <div className="md:col-span-2 xl:col-span-3">
                <button
                  onClick={clearFilters}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {t('common.clearFilters')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Access Logs List */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading && pagination === null ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t('accessLogs.status')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t('accessLogs.person')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t('accessLogs.details')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t('accessLogs.entryPoint')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                    {t('accessLogs.timestamp')}
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider w-20">
                    {t('accessLogs.photo')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <Loader2 className="w-7 h-7 animate-spin text-primary-400 mx-auto" />
                    </td>
                  </tr>
                ) : accessLogs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center">
                      <User className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                      <p className="text-slate-500">{t('accessLogs.noRecords')}</p>
                    </td>
                  </tr>
                ) : null}
                {!loading && accessLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                          log.status === 'GRANTED'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {log.status === 'GRANTED'
                            ? <CheckCircle className="w-4 h-4" />
                            : <XCircle className="w-4 h-4" />}
                          {log.status === 'GRANTED' ? 'Concedido' : 'Denegado'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-slate-500" />
                          </div>
                          <p className="font-medium text-slate-800">{log.userName || '-'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                            {log.method ? getMethodLabel(log.method) : '-'}
                          </span>
                          {log.plateNumber && (
                            <p className="flex items-center gap-1 text-sm text-slate-600">
                              <Car className="w-3 h-3" />
                              {log.plateNumber}
                            </p>
                          )}
                          {log.details && humanizeDetails(log.details, log.status, t) && (
                            <p className="text-sm text-slate-500">
                              {humanizeDetails(log.details, log.status, t)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-600">{log.entryPoint || '-'}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {formatTimestamp(log.timestamp)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {log.photoUrl ? (
                          <button
                            type="button"
                            onClick={() => setLightboxUrl(log.photoUrl!)}
                            className="focus:outline-none"
                          >
                            <img
                              src={log.photoUrl}
                              alt="Foto del acceso"
                              className="w-12 h-12 rounded-lg object-cover hover:ring-2 hover:ring-blue-400 transition-all"
                              onError={handleImgError}
                              loading="lazy"
                            />
                          </button>
                        ) : (
                          <Camera className="w-4 h-4 text-slate-300" />
                        )}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && pagination && pagination.total > 0 && (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-t border-slate-200">
              <div className="flex items-center gap-3">
                <p className="text-sm text-slate-600">
                  {t('accessLogs.paginationInfo', {
                    from: (pagination.page - 1) * pagination.limit + 1,
                    to: Math.min(pagination.page * pagination.limit, pagination.total),
                    total: pagination.total,
                  })}
                </p>
                <select
                  value={pagination.limit}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  className="text-sm border border-slate-300 rounded-lg px-2 py-1 bg-white"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-slate-500">{t('accessLogs.perPage')}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => goToPage(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="flex items-center gap-1 text-sm text-slate-600">
                  <span className="hidden sm:inline">{t('accessLogs.goToPage')}</span>
                  <input
                    type="number"
                    min={1}
                    max={pagination.totalPages}
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const page = parseInt(pageInput, 10);
                        if (page >= 1 && page <= pagination.totalPages) {
                          goToPage(page);
                        }
                      }
                    }}
                    className="w-14 text-center border border-slate-300 rounded-lg px-2 py-1"
                  />
                  <span>/ {pagination.totalPages}</span>
                </div>
                <button
                  onClick={() => goToPage(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                  className="p-2 rounded-lg hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          </>
        )}
      </div>
    </div>

    {lightboxUrl && (
      <div
        className="fixed inset-0 bg-black/95 flex items-center justify-center z-50"
        onClick={() => setLightboxUrl(null)}
      >
        <button
          type="button"
          onClick={() => setLightboxUrl(null)}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
        >
          <X className="text-white w-6 h-6" />
        </button>
        <img
          src={lightboxUrl}
          alt="Foto del acceso ampliada"
          className="max-w-full max-h-[85vh] rounded-lg object-contain"
          onClick={(e) => e.stopPropagation()}
          onError={() => { setLightboxUrl(null); handleImgError(); }}
        />
      </div>
    )}
    </>
  );
};
